/**
 * The sync engine.
 *
 * Local writes never wait for this. Dexie is the source of truth for the UI;
 * this pushes what changed and pulls what others changed, on a timer, and is
 * safe to interrupt at any point.
 *
 * Guarantees, and how each is achieved:
 *
 *  Idempotent      Rows carry client-generated UUIDs, so a push is an upsert on
 *                  a known primary key. Replaying the same push changes nothing.
 *
 *  Interruptible   `dirty` is cleared only AFTER the server confirms, and the
 *                  pull cursor advances only AFTER the Dexie write commits. A
 *                  process killed mid-run re-does work; it never skips it.
 *
 *  Atomic pulls    Each table's pulled page is applied in one Dexie transaction,
 *                  so a half-applied pull is impossible.
 *
 *  Never blocking  Everything is async and off the render path. Failure sets a
 *                  status the indicator reads; no dialogs, no thrown errors
 *                  reaching the UI.
 *
 * Conflict rule: last-write-wins on `updated_at` for members, subscriptions and
 * class_prices. Payments and walk-ins are append-only and cannot conflict —
 * which is exactly why money is modelled as a log rather than a mutable field.
 */
import { db, getSetting, setSetting, SETTING_KEYS } from '../db/db';
import { reseedSkIdCounter } from '../db/ids';
import { getSupabase, isSupabaseConfigured, SYNC_TABLES, type SyncTable } from '@shared/supabase';
import { computeClockOffsetMs, nowTimestamp, setClockOffsetMs } from '@shared/dates';
import type { Table } from 'dexie';

/** Rows per pull page. Keeps a cold start from holding one huge transaction. */
const PULL_PAGE_SIZE = 500;

/** The epoch we pull from when a device has never synced. */
const EPOCH = '1970-01-01T00:00:00.000Z';

/**
 * The config tables carry a natural key as well as an id, and Postgres enforces
 * it with a partial unique index (`price_schedule_key_live_uniq` and friends).
 *
 * That creates one collision this engine has to resolve. Every device seeds its
 * own copy of the 24-row price schedule on first boot, with its own UUIDs. The
 * SECOND device to sync therefore tries to INSERT a row whose `price_key`
 * already exists — Postgres answers 23505, `pushTable` throws, and the whole
 * run aborts. Left unhandled, a newly installed front desk PC can never sync at
 * all: it fails on the price schedule before it ever reaches the members.
 *
 * The resolution is to pull these tables BEFORE pushing anything (see runSync)
 * and reconcile here. A pulled row that shares a natural key with a local row
 * of a different id, where the local row has never reached the server, means
 * this device seeded a duplicate. The local row goes; the server's id wins.
 *
 * If that local row was EDITED here before the device's first sync, throwing it
 * away would silently discard a real price change, so its value is kept and
 * re-attached to the server's id — the next push then updates the server's row
 * instead of inserting a second one.
 */
const NATURAL_KEY: Partial<Record<SyncTable, { key: string; value: string }>> = {
  price_schedule: { key: 'price_key', value: 'price_piastres' },
  coach_assignments: { key: 'class_key', value: 'coach_name' },
  // The roster's natural key IS its only field, so there is no value to
  // preserve — a duplicate is always the same coach and the server's id wins.
  coaches: { key: 'name', value: 'name' },
  app_settings: { key: 'key', value: 'value' },
};

const CONFIG_TABLES = SYNC_TABLES.filter((t) => NATURAL_KEY[t]);

/**
 * Tables that are append-only in Postgres — INSERT is allowed, UPDATE is not
 * granted to anybody.
 *
 * These MUST be pushed as ON CONFLICT DO NOTHING rather than as an upsert. An
 * upsert takes the UPDATE path when the id already exists, and with no UPDATE
 * policy the database rejects it, which fails the whole push and leaves the
 * device unable to sync until someone works out why.
 *
 * A replay is the case that hits it: if the push lands but clearing `dirty`
 * does not — the tab closes, the machine loses power — the same payment goes
 * up again on the next run. DO NOTHING is also the correct SEMANTICS here, not
 * just the safe SQL: a payment row is written once locally and never edited, so
 * a second push of one is always a byte-identical replay with nothing to apply.
 */
const APPEND_ONLY_TABLES: readonly SyncTable[] = ['payments'];

/**
 * Whether a failure means "the internet is not there" rather than "something
 * is wrong".
 *
 * `navigator.onLine` only knows whether the network ADAPTER is up. The common
 * outage at the desk is the other kind — Wi-Fi connected, router fine, no
 * internet behind it — and there the browser reports online all day while
 * every request fails. Treating that as an error turned the pill red with
 * "Sync problem", which staff reasonably read as the system having stopped,
 * when every write was sitting safely in IndexedDB waiting to go up.
 *
 * Matched on the message because the errors cross two libraries and get
 * re-wrapped on the way (`Push members: TypeError: Failed to fetch`).
 */
export function isNetworkFailure(e: unknown): boolean {
  if (!e) return false;
  const name = (e as { name?: unknown }).name;
  if (name === 'AuthRetryableFetchError') return true;
  const message = e instanceof Error ? e.message : String((e as { message?: unknown }).message ?? e);
  return /failed to fetch|networkerror|network request failed|load failed|err_internet_disconnected|err_name_not_resolved|timeouterror|aborterror|timed out/i.test(
    message
  );
}

export interface SyncOutcome {
  status: 'ok' | 'error' | 'skipped' | 'offline';
  pushed: number;
  pulled: number;
  message: string;
}

let running = false;
let consecutiveFailures = 0;

/** Local-only bookkeeping columns that must never be sent to Postgres. */
function stripLocalFields<T extends Record<string, unknown>>(row: T): Omit<T, 'dirty'> {
  const { dirty: _dirty, ...rest } = row;
  return rest;
}

function tableFor(name: SyncTable): Table<Record<string, unknown>, string> {
  return db[name] as unknown as Table<Record<string, unknown>, string>;
}

function cursorKey(table: SyncTable): string {
  return `${SETTING_KEYS.syncCursorPrefix}${table}`;
}

/**
 * One full sync run: clock, then push, then pull.
 *
 * Push before pull so our own changes are on the server before we ask what
 * changed — otherwise a row we just wrote could come back as "remote" and
 * lose to a stale copy.
 */
export async function runSync(
  trigger: 'auto' | 'manual' | 'reconnect' | 'write' = 'auto'
): Promise<SyncOutcome> {
  if (running) {
    return { status: 'skipped', pushed: 0, pulled: 0, message: 'Already running' };
  }

  const supabase = getSupabase();
  if (!supabase) {
    return {
      status: 'skipped',
      pushed: 0,
      pulled: 0,
      message: 'Cloud sync is not configured (VITE_SUPABASE_URL).',
    };
  }

  if (!navigator.onLine) {
    return { status: 'skipped', pushed: 0, pulled: 0, message: 'Offline' };
  }

  running = true;
  const startedAt = nowTimestamp();
  const logId = await db.sync_log.add({
    started_at: startedAt,
    finished_at: null,
    status: 'running',
    pushed: 0,
    pulled: 0,
    message: `Triggered: ${trigger}`,
  });

  let pushed = 0;
  let pulled = 0;

  try {
    // Signed in? Without a session RLS rejects everything, and the useful
    // message is "not signed in", not a wall of 401s.
    const { data: session, error: sessionError } = await supabase.auth.getSession();
    if (!session.session) {
      // The access token lasts an hour. Past that, getSession() has to refresh
      // it over the network, and with no internet it comes back empty — which
      // is NOT being signed out: the stored session is kept and refreshes the
      // moment the connection returns. Saying "not signed in" here sent staff
      // to a sign-in form that cannot work offline either.
      if (isNetworkFailure(sessionError)) throw sessionError;
      throw new Error('Not signed in to the cloud. Open Settings → Sync to sign in.');
    }

    await syncClock();

    // Config first, before anything is pushed. A device that has just seeded
    // its own price schedule has to learn the server's ids before it tries to
    // insert its own, or the unique index rejects the push. See NATURAL_KEY.
    // On every later run these tables are already at their cursor and this
    // costs three empty requests.
    for (const table of CONFIG_TABLES) {
      pulled += await pullTable(table);
    }
    for (const table of SYNC_TABLES) {
      pushed += await pushTable(table);
    }
    for (const table of SYNC_TABLES) {
      pulled += await pullTable(table);
    }

    // A pull may have brought in members from another device; make sure the
    // local SK ID counter is past anything that arrived.
    if (pulled > 0) await reseedSkIdCounter();

    await setSetting(SETTING_KEYS.lastSyncedAt, nowTimestamp());
    consecutiveFailures = 0;

    await db.sync_log.update(logId, {
      finished_at: nowTimestamp(),
      status: 'ok',
      pushed,
      pulled,
      message: pushed || pulled ? `↑${pushed} ↓${pulled}` : 'Up to date',
    });

    return { status: 'ok', pushed, pulled, message: 'Synced' };
  } catch (e) {
    // Still backs off: a dead connection should not be hammered every 15s.
    consecutiveFailures++;
    const offline = isNetworkFailure(e);
    const status = offline ? 'offline' : 'error';
    const message = offline
      ? 'No internet. Everything is saved on this PC and will upload when it returns.'
      : e instanceof Error
        ? e.message
        : String(e);
    await db.sync_log.update(logId, {
      finished_at: nowTimestamp(),
      status,
      pushed,
      pulled,
      message,
    });
    return { status, pushed, pulled, message };
  } finally {
    running = false;
  }
}

/**
 * Read the server's clock and store the offset.
 *
 * Latency is halved out via the round-trip midpoint, so a slow connection does
 * not read as clock drift. See computeClockOffsetMs.
 */
async function syncClock(): Promise<void> {
  const supabase = getSupabase();
  if (!supabase) return;

  const sentAt = Date.now();
  const { data, error } = await supabase.rpc('server_time');
  const receivedAt = Date.now();
  if (error || !data) return; // Not fatal — keep the previous offset.

  const offset = computeClockOffsetMs(String(data), sentAt, receivedAt);
  setClockOffsetMs(offset);
  await setSetting(SETTING_KEYS.clockOffsetMs, String(offset));
}

/**
 * Push every dirty row in a table.
 *
 * `dirty` is cleared only for rows whose `updated_at` has not changed since we
 * read them. If the front desk edited a member while the request was in
 * flight, that row stays dirty and goes up on the next run — the edit is not
 * silently dropped.
 */
async function pushTable(table: SyncTable): Promise<number> {
  const supabase = getSupabase();
  if (!supabase) return 0;

  const local = tableFor(table);
  const dirtyRows = await local.where('dirty').equals(1).toArray();
  if (dirtyRows.length === 0) return 0;

  const payload = dirtyRows.map(stripLocalFields);

  const { error } = await supabase.from(table).upsert(payload, {
    onConflict: 'id',
    // Append-only tables have no UPDATE policy, so the upsert must never take
    // the UPDATE path — see APPEND_ONLY_TABLES. Everywhere else we are the
    // writer and the newer local row wins.
    ignoreDuplicates: APPEND_ONLY_TABLES.includes(table),
  });
  if (error) throw new Error(`Push ${table}: ${error.message}`);

  const confirmed = new Map(dirtyRows.map((r) => [r.id as string, r.updated_at as string]));
  await db.transaction('rw', local, async () => {
    for (const [id, updatedAt] of confirmed) {
      const current = await local.get(id);
      // Changed again while we were pushing — leave it dirty for the next run.
      if (current && current.updated_at === updatedAt) {
        await local.update(id, { dirty: 0 });
      }
    }
  });

  return dirtyRows.length;
}

/**
 * Pull everything changed since our cursor, in pages.
 *
 * The cursor advances only after each page's Dexie transaction commits, so an
 * interrupted pull resumes from the last page that actually landed.
 */
async function pullTable(table: SyncTable): Promise<number> {
  const supabase = getSupabase();
  if (!supabase) return 0;

  const local = tableFor(table);
  let cursor = (await getSetting(cursorKey(table))) ?? EPOCH;
  let total = 0;

  for (;;) {
    const { data, error } = await supabase
      .from(table)
      .select('*')
      .gt('updated_at', cursor)
      .order('updated_at', { ascending: true })
      .limit(PULL_PAGE_SIZE);

    if (error) throw new Error(`Pull ${table}: ${error.message}`);
    if (!data || data.length === 0) break;

    // One transaction per page: a half-applied page is impossible.
    await db.transaction('rw', local, async () => {
      // Config tables only: index this device's rows by natural key once per
      // page rather than once per row. These tables are tiny (24 prices, 14
      // coaches, 5 settings), so reading them whole is cheaper than a lookup
      // per remote row.
      const natural = NATURAL_KEY[table];
      const byNaturalKey = new Map<string, Record<string, unknown>[]>();
      if (natural) {
        for (const row of await local.toArray()) {
          if (row.deleted_at !== null) continue; // Excluded from the unique index.
          const k = String(row[natural.key]);
          const bucket = byNaturalKey.get(k);
          if (bucket) bucket.push(row);
          else byNaturalKey.set(k, [row]);
        }
      }

      for (const remote of data as Record<string, unknown>[]) {
        const id = remote.id as string;
        const existing = await local.get(id);

        // Last-write-wins. A local row that is still dirty and NEWER than the
        // remote copy keeps its edit — it will win on the next push too.
        if (
          existing &&
          existing.dirty === 1 &&
          String(existing.updated_at) > String(remote.updated_at)
        ) {
          continue;
        }

        let incoming: Record<string, unknown> = { ...remote, dirty: 0 };

        if (natural) {
          const duplicates = (byNaturalKey.get(String(remote[natural.key])) ?? []).filter(
            (r) => r.id !== id && r.dirty === 1
          );
          for (const duplicate of duplicates) {
            // Untouched since it was seeded means it is a default this device
            // generated, not a decision anyone made — the server's copy wins.
            // Otherwise keep the value and adopt the server's id, so the next
            // push UPDATEs rather than colliding on the unique index again.
            if (duplicate.created_at !== duplicate.updated_at) {
              incoming = {
                ...incoming,
                [natural.value]: duplicate[natural.value],
                updated_at: duplicate.updated_at,
                dirty: 1,
              };
            }
            await local.delete(duplicate.id as string);
          }
        }

        await local.put(incoming);
      }
    });

    total += data.length;
    const last = data[data.length - 1] as Record<string, unknown> | undefined;
    const nextCursor = last ? String(last.updated_at) : cursor;

    // Only now is it safe to remember where we got to.
    await setSetting(cursorKey(table), nextCursor);

    if (nextCursor === cursor) break; // No forward progress — stop rather than loop.
    cursor = nextCursor;

    if (data.length < PULL_PAGE_SIZE) break;
  }

  return total;
}

/* -------------------------------------------------------------------------
   Sync-on-write
   ------------------------------------------------------------------------- */

let writeDebounce: ReturnType<typeof setTimeout> | null = null;

/**
 * Ask for a sync because something was just written.
 *
 * Debounced by a second, so saving a member + subscription + payment in one
 * transaction triggers ONE run rather than three. Fire-and-forget: this never
 * returns a promise the caller could accidentally await, because the whole
 * point is that saving does not wait for the network.
 */
export function requestSync(): void {
  if (!isSupabaseConfigured || !navigator.onLine) return;
  if (writeDebounce) clearTimeout(writeDebounce);
  writeDebounce = setTimeout(() => {
    writeDebounce = null;
    void runSync('write');
  }, 1000);
}

/* -------------------------------------------------------------------------
   Scheduling
   ------------------------------------------------------------------------- */

/** Base interval between automatic runs. */
const BASE_INTERVAL_MS =
  Number(import.meta.env.VITE_SYNC_INTERVAL_SECONDS ?? 15) * 1000 || 15_000;

const MAX_BACKOFF_MS = 5 * 60 * 1000;

/**
 * Exponential backoff after failures, capped at five minutes.
 *
 * A gym with flaky internet should not hammer a dead endpoint every 15
 * seconds, but should recover quickly once it comes back — which the `online`
 * listener handles by resetting immediately.
 */
export function nextDelayMs(): number {
  if (consecutiveFailures === 0) return BASE_INTERVAL_MS;
  return Math.min(BASE_INTERVAL_MS * 2 ** consecutiveFailures, MAX_BACKOFF_MS);
}

let timer: ReturnType<typeof setTimeout> | null = null;
let stopped = true;

/** Start the background loop. Safe to call twice. */
export function startSyncLoop(): void {
  if (!stopped) return;
  stopped = false;

  const tick = async () => {
    if (stopped) return;
    await runSync('auto');
    if (!stopped) timer = setTimeout(tick, nextDelayMs());
  };

  // Restore the stored clock offset immediately, so "today" is right even
  // before the first successful sync.
  void getSetting(SETTING_KEYS.clockOffsetMs).then((v) => {
    if (v !== null) setClockOffsetMs(Number(v));
  });

  const onReconnect = () => {
    consecutiveFailures = 0; // The network is back; do not stay backed off.
    void runSync('reconnect');
  };
  window.addEventListener('online', onReconnect);

  // `online` does not fire in every case — a laptop waking from sleep or a
  // captive portal that starts working can both go unannounced. Re-checking
  // when the window regains focus catches those.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && navigator.onLine) {
      consecutiveFailures = 0;
      void runSync('reconnect');
    }
  });

  timer = setTimeout(tick, 1000); // Let the first paint land first.
}

export function stopSyncLoop(): void {
  stopped = true;
  if (timer) clearTimeout(timer);
  timer = null;
}

export { isSupabaseConfigured };
